import React, { useState, useEffect, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";
import axiosInstance from "../axios";
import EditPromoCode from "./EditPromoCode";
import ModernDataTable from "../common/GeneralComponents/ModernDataTable";

function PromoCodeDatatable({ HandleRender }) {
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState([]);
	const [render, setRender] = useState(false);
	const [expandedRows, setExpandedRows] = useState(null);

	// Server-side pagination state
	const [totalRecords, setTotalRecords] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [nextPage, setNextPage] = useState(null);
	const [prevPage, setPrevPage] = useState(null);
	const [searchTerm, setSearchTerm] = useState("");

	// Fetch data function
	const fetchData = useCallback((page = 1, size = 10, search = "") => {
		setLoading(true);
		let url = `/admin/promo-codes?is_deleted=false&page=${page}&page_size=${size}`;
		if (search) {
			url += `&search=${encodeURIComponent(search)}`;
		}
		axiosInstance
			.get(url)
			.then((res) => {
				setData(res.data.results);
				setTotalRecords(res.data.count);
				setNextPage(res.data.next);
				setPrevPage(res.data.previous);
				setLoading(false);
			})
			.catch((err) => {
				setLoading(false);
				toast.error("حدث خطأ في تحميل البيانات");
			});
	}, []);

	// Initial load and reload on render change
	useEffect(() => {
		fetchData(currentPage, pageSize, searchTerm);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [HandleRender, render]);

	// Handle page change
	const handlePageChange = (page, size) => {
		setCurrentPage(page);
		setPageSize(size);
		fetchData(page, size, searchTerm);
	};

	// Handle search
	const handleSearch = (search) => {
		setSearchTerm(search);
		setCurrentPage(1); // Reset to first page on search
		fetchData(1, pageSize, search);
	};

	const rowExpansionTemplate = (row) => {
		return (
			<div className="d-flex justify-content-between p-3">
				<div>
					<h5>حالة الكود</h5>
					<hr />
					<span
						className={`badge ${row.is_active ? "bg-success" : "bg-danger"} p-2`}>
						{row.is_active === true ? "مفعل" : "غير مفعل"}
					</span>
				</div>
				<div className="d-flex gap-2 align-items-start">
					<button
						onClick={() => {
							if (
								window.confirm(
									`هل انت متأكد من ${
										row.is_active === true ? "تعطيل" : "تفعيل"
									} الكود ؟`,
								)
							) {
								axiosInstance
									.patch(`/admin/promo-codes/${row.id}`, {
										is_active: !row.is_active,
									})
									.then((res) => {
										toast.success("تم تعديل حالة الكود بنجاح");
										setRender(!render);
									})
									.catch((err) => {
										toast.error("حدث خطأ");
									});
							}
						}}
						className="btn btn-secondary">
						{row.is_active ? "تعطيل" : "تفعيل"}
					</button>
					<button
						onClick={() => {
							if (window.confirm(`هل انت متأكد من حذف الكود "${row.code}" ؟`)) {
								axiosInstance
									.patch(`/admin/promo-codes/${row.id}`, {
										is_deleted: true,
									})
									.then((res) => {
										toast.success("تم حذف الكود بنجاح");
										setRender(!render);
									})
									.catch((err) => {
										toast.error("حدث خطأ في الحذف");
									});
							}
						}}
						className="btn btn-danger">
						حذف
					</button>
					<EditPromoCode data={row} setRender={setRender} render={render} />
				</div>
			</div>
		);
	};

	// Column configurations for the table
	const columns = [
		{
			field: "id",
			header: "كود",
		},
		{
			field: "code",
			header: "الكود الترويجي",
		},
		{
			field: "promo_type",
			header: "النوع",
			body: (row) => (
				<span>
					{row.promo_type === "shipping"
						? "شحن مجانى"
						: row.promo_type === "discount_percentage"
							? "خصم نسبة"
							: "خصم مبلغ"}
				</span>
			),
		},
		{
			field: "discount_value",
			header: "القيمة",
			body: (row) => (
				<span>
					{row.promo_type === "shipping"
						? "شحن مجانى"
						: row.promo_type === "discount_percentage"
							? row.discount_percentage + " %"
							: row.discount_amount + " جنيه"}
				</span>
			),
		},
		{
			field: "usage_limit",
			header: "عدد مرات الاستخدام",
		},
		{
			field: "discount_amount_min_amount",
			header: "الحد الادنى",
		},
		{
			field: "usage_left",
			header: "المتبقى",
		},
	];

	return (
		<>
			<ModernDataTable
				data={data}
				columns={columns}
				loading={loading}
				searchPlaceholder="ابحث عن كود ترويجي..."
				showSearch={true}
				showPagination={true}
				rowsPerPage={pageSize}
				rowsPerPageOptions={[5, 10, 25, 50]}
				expandable={true}
				expandedRows={expandedRows}
				onRowToggle={(e) => setExpandedRows(e.data)}
				rowExpansionTemplate={rowExpansionTemplate}
				emptyMessage="لا توجد أكواد ترويجية"
				// Server-side props
				totalRecords={totalRecords}
				currentPage={currentPage}
				nextPage={nextPage}
				prevPage={prevPage}
				onPageChange={handlePageChange}
				onSearch={handleSearch}
			/>
			<ToastContainer autoClose={2000} rtl />
		</>
	);
}

export default PromoCodeDatatable;
